import { BusinessObjectBase } from './base';
import type { Candidate } from './candidate';
import type { CorrespondenceItem } from './correspondence-item';
import type { Interview } from './interview';
import type { MyUserTask } from './my-user-task';
import type { RoleAssignment } from './role-assignment';
import type { TrainingClass } from './training-class';
import type { UserTask } from './user-task';

export interface UserData {
  readonly id: number;
  readonly email: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly phone?: string;
  readonly roleAssignments?: RoleAssignment[];
  readonly userTasks?: UserTask[];
  readonly myUserTasks?: MyUserTask[];
  readonly candidate?: Candidate;
  readonly trainingClasses?: TrainingClass[];
  readonly interviews?: Interview[];
  readonly sentCorrespondenceItems?: CorrespondenceItem[];
  readonly receivedCorrespondenceItems?: CorrespondenceItem[];
}

export interface User extends Readonly<UserData> {}
export class User extends BusinessObjectBase {
  static readonly entityName = 'User' as const;
  static readonly plural = 'users';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<User[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new User(data));
  }

  static async findOne(filter?: any): Promise<User | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new User(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<User> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new User(data);
  }

  static async create(data: Omit<UserData, 'id'>): Promise<User> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new User(result);
  }

  static async updateById(id: number, data: Partial<UserData>): Promise<User> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new User(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async currentUser(): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/current-user`;
    return this.get<any>(url);
  }
  static async updateProfile(body: {
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Promise<Promise<User | null>> {
    const url = `${this.baseUrl}/${this.plural}/update-profile`;
    return this.post<Promise<User | null>>(url, body);
  }
}
